import React from "react";
import {
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import * as XLSX from "xlsx";
import { Download } from '@mui/icons-material';

export default function MetricsPanel({ metrics, shockSelection = ['No Shock'], onShockSelectionChange, region, asof, securitySelection, assetData = [], dynamicColumns = [], reinvestmentYield = 0 }) {
  if (!metrics || !metrics.data || Object.keys(metrics.data).length === 0) {
    return (
      <Typography variant="body1" color="text.secondary">
        No metrics data available.
      </Typography>
    );
  }

  // Check if Up 100 shock should be enabled (same logic as Main.jsx)
  const isShockEnabled = (() => {
    if (region.includes('SPDA')) {
      return asof === '2025-05-28';
    }
    if (region === 'FIA NY') {
      return new Date(asof) >= new Date('2025-07-16');
    }
    return false;
  })();

  // Hardcoded values for specific metrics
  const hardcodedValues = {
    "Reinvestment Yield": reinvestmentYield === 0 ? "N/A" : reinvestmentYield,
    "Investment Expense": 0.003,
    "Sec. Selection (bps)": securitySelection,
    "Tax Rate": 0.21,
    "Debt to Capital": 0.25,
    "Cost of Debt": 0.08,
  };

  // Map of metric keys to section and display field
  // Define sections and their fields with formats
  const metricDefinitions = {
    "Day 1": {
      "Gross Yield": { key: "day1_gross_yield (w/ sec selection)", format: "percent" },
      "Spread (ex. SS)": { key: "day1_spread", format: "bps" },
      "WAL": {key: "day1_wal", format: "years"},
      "Investment Expense": { key: "investment_expense", format: "percent" },
      "Default Rate": { key: "default_cost", format: "percent" },
      "Net Yield": { key: "net_yield", format: "percent" }
    },
    "NIER": {
      "NIER": { key: "wavg_nier", format: "percent" },
      "CoF": { key: "cof", format: "percent" },
      "ROA (pre-tax)": { key: "roa_pre_tax", format: "percent" },
      "Reinvestment Yield": { key: "reinv_yield", format: "percent" }
    },
    "Other": {
      "Sec. Selection (bps)": { key: "yield_upside", format: "bps" },
      "C1o (wavg)": { key: "wavg_c1o", format: "percent" },
      "C1cs (wavg)": { key: "wavg_c1cs", format: "percent" },
      "S&P Charge (wavg)": { key: "wavg_snp_charge", format: "percent" },
      "Liability Duration": { key: "lia_dur", format: "years" },
      "Asset Duration": { key: "asset_dur", format: "years" },
      "Mismatch": { key: "alm mismatch", format: "years" }
    },
    "IRR": {
      "Unlevered": { key: "unlev_irr", format: "percent" },
      "Levered": { key: "lev_irr", format: "percent" }
    },
    "Liability": {
      "Shock Lapse": { key: "shock_lapse", format: "percent" }
    },
    "Corporate": {
      "Tax Rate": { key: "tax_rate", format: "percent" },
      "Debt to Capital": { key: "debt_to_capital", format: "percent" },
      "Cost of Debt": { key: "cost_of_debt", format: "percent" }
    }
  };

  // Create metricsMap from metricDefinitions
  const metricsMap = {};
  const desiredOrder = [];
  
  Object.entries(metricDefinitions).forEach(([section, fields]) => {
    Object.entries(fields).forEach(([field, { key, format }]) => {
      metricsMap[key] = { section, field, format };
      desiredOrder.push(field);
    });
  });

  // Build a flat array of {section, field, key, format, isFirst} in the desired order
  const allRows = [];
  desiredOrder.forEach((field) => {
    // Find the key in metricsMap that matches this field
    const entry = Object.entries(metricsMap).find(([_key, val]) => val.field === field);
    if (entry) {
      const [key, { section, format }] = entry;
      allRows.push({ section, field, key, format });
    }
  });

  // Helper to format values for display
  function formatValue(value, format, section) {
    if (section == "Corporate") {
      return (value * 100).toFixed(0) + "%";
    }
    if (format === "percent") return (value * 100).toFixed(2) + "%";
    if (format === "bps") return (value).toFixed(0);
    if (format === "years" || format === "number") return value.toFixed(2);
    return value;
  }

  // Helper function to get the correct data field based on shock selection
  function getDataField(baseField, shock) {
    if (shock === 'Up 100 shock') {
      return `${baseField}_up`;
    } else if (shock === 'Down 100 shock') {
      return `${baseField}_down`;
    }
    return baseField; // No Shock
  }

  // Get all product keys
  const productKeys = Object.keys(metrics.data);

  // For each product, determine if any metric has a nonzero variance for any selected shock
  const productShowVariance = {};
  productKeys.forEach(productKey => {
    const productData = metrics.data[productKey] || {};
    productShowVariance[productKey] = false;
    
    // Check each shock selection
    shockSelection.forEach(shock => {
      const originalField = getDataField('original', shock);
      const updatedField = getDataField('updated', shock);
      const original = productData[originalField] || {};
      const updated = productData[updatedField] || {};
      
      const hasVariance = allRows.some(({ field, key }) => {
        const origValue = getMetricValue(field, key, original);
        const updValue = getMetricValue(field, key, updated);
        return updValue !== origValue;
      });
      
      if (hasVariance) {
        productShowVariance[productKey] = true;
      }
    });
  });

  // If any product has variance, show subcolumns for all
  const showAnyVariance = Object.values(productShowVariance).some(Boolean);


  function getMetricValue(field, key, source) {
    if (field === "Net Yield") {
      // Net Yield = Gross Yield - Investment Expense - Default Rate
      return (
        (source["day1_gross_yield (w/ sec selection)"] ?? 0) -
        hardcodedValues["Investment Expense"] -
        (source["default_cost"] / 10000 ?? 0)
      );
    }
    if (field === "ROA (pre-tax)") {
      // ROA = NIER - CoF
      return (source["wavg_nier"] ?? 0) - (source["cof"] ?? 0);
    }
    if (field === "Default Rate") {
      // Scale default_cost to decimal if needed
      const val = source[key] ?? 0;
      return val > 1 ? val / 10000 : val;
    }
    if (hardcodedValues.hasOwnProperty(field)) {
      return hardcodedValues[field];
    }
    // Default: use the value from metrics
    return source[key] ?? 0;
  }

  // Download handler for Excel export
  const handleDownload = () => {
    const downloadData = [];
    
    // Add regular metrics data
    allRows.forEach(({ section, field, key, format }) => {
      const row = { Section: section, Metric: field };
      productKeys.forEach(productKey => {
        const productData = metrics.data[productKey] || {};
        
        shockSelection.forEach(shock => {
          const originalField = getDataField('original', shock);
          const updatedField = getDataField('updated', shock);
          const original = productData[originalField] || {};
          const updated = productData[updatedField] || {};
          const origValue = getMetricValue(field, key, original);
          const updValue = getMetricValue(field, key, updated);
          const variance = updValue - origValue;
          
          const shockLabel = shock === 'No Shock' ? '' : ` (${shock})`;
          if (productShowVariance[productKey] || showAnyVariance) {
            row[`${productKey}${shockLabel} (Original)`] = origValue;
            row[`${productKey}${shockLabel} (Sensitivity)`] = updValue;
            row[`${productKey}${shockLabel} (Variance)`] = variance;
          } else {
            row[`${productKey}${shockLabel}`] = origValue;
          }
        });
      });
      downloadData.push(row);
    });

    // Add Asset Allocation data
    if (assetData.length > 0 && dynamicColumns.length > 0) {
      // Get unique asset names that have non-zero weights
      const assetNames = new Set();
      assetData.forEach(asset => {
        dynamicColumns.forEach(col => {
          const weight = asset.weights?.[col.field] || 0;
          if (weight > 0) {
            assetNames.add(asset.name || asset.cusip || "unknown");
          }
        });
      });

      // Add asset allocation rows
      Array.from(assetNames).sort().forEach(assetName => {
        const row = { Section: "Asset Allocation", Metric: assetName };
        
        productKeys.forEach(productKey => {
          // Find the dynamic column that matches this productKey
          const dynamicCol = dynamicColumns.find(col => col.headerName === productKey);
          if (dynamicCol) {
            // Find the asset with this name/cusip
            const asset = assetData.find(a => (a.name || a.cusip || "unknown") === assetName);
            const weight = asset?.weights?.[dynamicCol.field] || 0;
            const weightPercent = (weight * 100).toFixed(2) + '%';
            
            shockSelection.forEach(shock => {
              const shockLabel = shock === 'No Shock' ? '' : ` (${shock})`;
              if (productShowVariance[productKey] || showAnyVariance) {
                row[`${productKey}${shockLabel} (Original)`] = weightPercent;
                row[`${productKey}${shockLabel} (Sensitivity)`] = weightPercent;
                row[`${productKey}${shockLabel} (Variance)`] = '0.00%';
              } else {
                row[`${productKey}${shockLabel}`] = weightPercent;
              }
            });
          }
        });
        downloadData.push(row);
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(downloadData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Metrics");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "metrics.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // define the overall display order once
  const shockOrder = ['Down 100 shock', 'No Shock', 'Up 100 shock'];
  // filter that order by whatever the user actually has selected
  const orderedShocks = shockOrder.filter(s => shockSelection.includes(s));

  return (
    <Card variant="outlined" sx={{ width: "100%", height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Metrics</Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="metrics-shock-label">Schock Scenarios</InputLabel>
              <Select
                labelId="metrics-shock-label"
                multiple
                value={shockSelection}
                label="Shock Scenarios"
                onChange={(e) => onShockSelectionChange && onShockSelectionChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                renderValue={(selected) =>
                  // always show in Down → No → Up
                   shockOrder.filter(s => selected.includes(s)).join(', ')
                }
              >
                <MenuItem value="No Shock">
                  <Checkbox checked={shockSelection.indexOf('No Shock') > -1} />
                  <ListItemText primary="No Shock" />
                </MenuItem>
                <MenuItem value="Up 100 shock" disabled={!isShockEnabled}>
                  <Checkbox checked={shockSelection.indexOf('Up 100 shock') > -1} />
                  <ListItemText 
                    primary="Up 100 shock" 
                    secondary={!isShockEnabled ? "Only available for SCP on 5/28/2025" : undefined}
                  />
                </MenuItem>
                <MenuItem value="Down 100 shock" disabled={!isShockEnabled}>
                  <Checkbox checked={shockSelection.indexOf('Down 100 shock') > -1} />
                  <ListItemText 
                    primary="Down 100 shock" 
                    secondary={!isShockEnabled ? "Only available for SCP on 5/28/2025" : undefined}
                  />
                </MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleDownload}
              startIcon={<Download />}
              sx={{ backgroundColor: "lightgrey", color: "black" }}
            >
              Download
            </Button>
          </Box>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Section</TableCell>
                <TableCell>Metric</TableCell>
                {productKeys.flatMap(productKey =>
                  orderedShocks.map(shock => {
                    const shockLabel = shock === 'No Shock' ? productKey : `${productKey} (${shock})`;
                    return productShowVariance[productKey] || showAnyVariance ? (
                      <TableCell key={`${productKey}-${shock}-orig`} align="center" colSpan={3}>{shockLabel}</TableCell>
                    ) : (
                      <TableCell key={`${productKey}-${shock}-single`} align="center">{shockLabel}</TableCell>
                    );
                  })
                )}
              </TableRow>
              {showAnyVariance && (
                <TableRow>
                  <TableCell colSpan={2} />
                  {productKeys.flatMap(productKey =>
                    orderedShocks.map(shock => 
                      productShowVariance[productKey] || showAnyVariance ? (
                        <>
                          <TableCell key={`${productKey}-${shock}-orig-sub`} align="center">Original</TableCell>
                          <TableCell key={`${productKey}-${shock}-sens-sub`} align="center">Sensitivity</TableCell>
                          <TableCell key={`${productKey}-${shock}-var-sub`} align="center">Variance</TableCell>
                        </>
                      ) : (
                        <TableCell key={`${productKey}-${shock}-single-sub`} />
                      )
                    )
                  )}
                </TableRow>
              )}
            </TableHead>
            <TableBody>
              {allRows.map(({ section, field, key, format }, idx) => (
                <TableRow 
                  key={section + field}
                  sx={{
                    borderBottom: idx === allRows.length - 1 || allRows[idx + 1].section !== section 
                      ? '2px solid #ccc'
                      : '1px solid rgba(224, 224, 224, 1)'
                  }}
                >
                  <TableCell>
                    {idx === 0 || allRows[idx - 1].section !== section ? section : ""}
                  </TableCell>
                  <TableCell>{field}</TableCell>
                  {productKeys.flatMap(productKey => 
                    orderedShocks.map(shock => {
                      const productData = metrics.data[productKey] || {};
                      const originalField = getDataField('original', shock);
                      const updatedField = getDataField('updated', shock);
                      const original = productData[originalField] || {};
                      const updated = productData[updatedField] || {};
                      const origValue = getMetricValue(field, key, original);
                      const updValue = getMetricValue(field, key, updated);
                      const variance = updValue - origValue;
                      
                      if (productShowVariance[productKey] || showAnyVariance) {
                        return (
                          <>
                            <TableCell key={`${productKey}-${shock}-orig-val`} align="center">{formatValue(origValue, format, section)}</TableCell>
                            <TableCell key={`${productKey}-${shock}-sens-val`} align="center">{formatValue(updValue, format, section)}</TableCell>
                            <TableCell key={`${productKey}-${shock}-var-val`} align="center" style={{ color: variance > 0 ? "green" : variance < 0 ? "red" : undefined }}>{formatValue(variance, format, section)}</TableCell>
                          </>
                        );
                      } else {
                        return (
                          <TableCell key={`${productKey}-${shock}-single-val`} align="center">{formatValue(origValue, format, section)}</TableCell>
                        );
                      }
                    })
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  );
}
